import type { AreaGuide } from "./types"

export const BATH_AREA_GUIDES: AreaGuide[] = [
  {
    slug: "bath-city-centre",
    name: "Bath City Centre",
    location: "bath",
    tagline: "World Heritage living at your doorstep",
    description:
      "Bath's city centre is a UNESCO World Heritage Site, offering a rare opportunity to live among some of Britain's finest Georgian architecture. The Royal Crescent, The Circus, Pulteney Bridge, and the Roman Baths are all within walking distance of the residential streets.\n\nProperty ranges from grand Georgian townhouses to modern apartments and period conversions. The city centre suits professionals, couples, and overseas buyers drawn to Bath's international reputation, cultural scene, and walkable lifestyle.",
    avgPrice: "£500,000",
    priceRange: "£200,000 - £3,000,000",
    propertyTypes: ["Georgian townhouses", "Period conversions", "Luxury apartments", "Mews houses"],
    transport: {
      nearestStation: "Bath Spa (direct to London Paddington, 80 mins)",
      commuteTime: "80 mins to London Paddington",
      busRoutes: "Extensive local network from bus station",
      parking: "Park & Ride recommended; limited city centre parking",
    },
    lifestyle: {
      restaurants: "The Olive Tree, Menu Gordon Jones, Sally Lunn's",
      schools: "King Edward's School, Royal High School, Hayesfield Girls",
      greenSpaces: "Royal Victoria Park, Parade Gardens, Sydney Gardens",
      shopping: "Milsom Street, SouthGate, independent arcades",
    },
    highlights: [
      "UNESCO World Heritage Site architecture",
      "Thermae Bath Spa and Roman Baths",
      "Direct trains to London Paddington",
      "Exceptional restaurant and cultural scene",
      "Walkable to everything",
    ],
    bestFor: ["Professionals", "Couples", "International buyers"],
    imageUrl: "/images/areas/bath/bath-city-centre-800w.webp",
  },
  {
    slug: "lansdown",
    name: "Lansdown",
    location: "bath",
    tagline: "Elevated living with panoramic views",
    description:
      "Lansdown sits on the hill above Bath, offering elevated views across the city and surrounding countryside. The area is characterised by Georgian and Victorian properties, many with large gardens and commanding positions. Lansdown Crescent is one of Bath's most prestigious addresses.\n\nThe area attracts established families and professionals who want space, views, and proximity to top schools. It's quieter than the city centre but only a short walk downhill to shops and restaurants.",
    avgPrice: "£650,000",
    priceRange: "£300,000 - £3,500,000",
    propertyTypes: ["Georgian houses", "Victorian villas", "Detached family homes", "Character cottages"],
    transport: {
      nearestStation: "Bath Spa (1 mile downhill)",
      commuteTime: "15 mins walk to city centre (downhill)",
      busRoutes: "2, 702 to city centre",
      parking: "Most properties have off-street parking",
    },
    lifestyle: {
      restaurants: "The Hare & Hounds, Lansdown Road eateries",
      schools: "Kingswood School, Royal High School, Lansdown Park",
      greenSpaces: "Lansdown Hill, Beckford's Tower, racecourse",
      shopping: "Local village shops; city centre nearby",
    },
    highlights: [
      "Panoramic views across Bath and the countryside",
      "Lansdown Crescent — one of Bath's finest addresses",
      "Beckford's Tower and gardens",
      "Proximity to top independent schools",
      "Large properties with generous gardens",
    ],
    bestFor: ["Families", "Upsizers", "Professionals"],
    imageUrl: "/images/areas/bath/lansdown-800w.webp",
  },
  {
    slug: "widcombe",
    name: "Widcombe",
    location: "bath",
    tagline: "Hillside village a stone's throw from the centre",
    description:
      "Widcombe is a charming residential area just south of Bath city centre, climbing the hill from the River Avon. It has a strong village identity with its own parade of shops, a weekly food market, and a community that punches above its weight for a neighbourhood of its size.\n\nProperty includes Georgian cottages, Victorian terraces, and hillside homes with views over the city. Widcombe suits buyers who want character, community, and walkable access to Bath Spa station and the city centre.",
    avgPrice: "£475,000",
    priceRange: "£225,000 - £1,200,000",
    propertyTypes: ["Georgian cottages", "Victorian terraces", "Hillside houses", "Period conversions"],
    transport: {
      nearestStation: "Bath Spa (5 mins walk)",
      commuteTime: "5 mins walk to Bath Spa station",
      busRoutes: "4, 265 to city centre",
      parking: "Residents' permits; hilly streets",
    },
    lifestyle: {
      restaurants: "The White Hart, Widcombe Deli, local cafes",
      schools: "Widcombe Junior School, Prior Park College (nearby)",
      greenSpaces: "Prior Park Landscape Garden (National Trust), Kennet & Avon Canal",
      shopping: "Widcombe Parade independents",
    },
    highlights: [
      "Walking distance to Bath Spa station",
      "Prior Park Landscape Garden by the National Trust",
      "Widcombe food market and community events",
      "Charming village atmosphere within the city",
      "Views over Bath from hillside properties",
    ],
    bestFor: ["Commuters", "Professionals", "Couples"],
    imageUrl: "/images/areas/bath/widcombe-800w.webp",
  },
  {
    slug: "bear-flat",
    name: "Bear Flat",
    location: "bath",
    tagline: "Independent spirit on the southern slopes",
    description:
      "Bear Flat is a characterful neighbourhood on the southern slopes of Bath, named after the Bear pub at its centre. The area has a strong independent streak with a good mix of local shops, cafes, and a popular deli. Wells Road forms the main thoroughfare.\n\nThe area offers good value compared to the city centre, with a mix of Georgian and Victorian properties, many with gardens and views. It's popular with young families and professionals who appreciate its community feel.",
    avgPrice: "£400,000",
    priceRange: "£200,000 - £900,000",
    propertyTypes: ["Victorian terraces", "Georgian houses", "Period conversions", "Family homes"],
    transport: {
      nearestStation: "Bath Spa (15 mins walk)",
      commuteTime: "15 mins walk to city centre",
      busRoutes: "4, 265 along Wells Road",
      parking: "Mix of on-street and off-street",
    },
    lifestyle: {
      restaurants: "The Bear, Locanta, Bear Flat Deli",
      schools: "Widcombe Junior School, Hayesfield Girls",
      greenSpaces: "Alexandra Park (panoramic views), Beechen Cliff",
      shopping: "Wells Road independents, Moorland Road shops",
    },
    highlights: [
      "Alexandra Park — one of Bath's best viewpoints",
      "Strong independent shopping and dining",
      "Good value compared to city centre",
      "Community atmosphere with local events",
      "Walking distance to the centre (downhill)",
    ],
    bestFor: ["Young families", "First-time buyers", "Professionals"],
    imageUrl: "/images/areas/bath/bear-flat-800w.webp",
  },
  {
    slug: "bathwick",
    name: "Bathwick",
    location: "bath",
    tagline: "Elegant east Bath with canal and gardens",
    description:
      "Bathwick is one of Bath's most desirable residential areas, stretching east from Pulteney Bridge along Great Pulteney Street — one of the grandest thoroughfares in England. The area includes Sydney Gardens, the Kennet & Avon Canal, and the Holburne Museum.\n\nProperty is predominantly Georgian, with grand townhouses, elegant terraces, and a small number of modern developments. Bathwick attracts buyers who want period grandeur with walkable access to the city centre.",
    avgPrice: "£600,000",
    priceRange: "£250,000 - £2,500,000",
    propertyTypes: ["Georgian townhouses", "Grand terraces", "Period conversions", "Garden apartments"],
    transport: {
      nearestStation: "Bath Spa (10 mins walk)",
      commuteTime: "10 mins walk to city centre across Pulteney Bridge",
      busRoutes: "4, 18 to city centre",
      parking: "Residents' permits in most streets",
    },
    lifestyle: {
      restaurants: "The Holburne Cafe, Bathwick Boatman, local options",
      schools: "Bathwick St Mary Primary, King Edward's (nearby)",
      greenSpaces: "Sydney Gardens, Kennet & Avon Canal, Bathwick Meadows",
      shopping: "Great Pulteney Street, city centre via Pulteney Bridge",
    },
    highlights: [
      "Great Pulteney Street — one of England's grandest streets",
      "Sydney Gardens and Kennet & Avon Canal",
      "Holburne Museum on the doorstep",
      "Elegant Georgian architecture throughout",
      "Peaceful yet central location",
    ],
    bestFor: ["Professionals", "Downsizers", "International buyers"],
    imageUrl: "/images/areas/bath/bathwick-800w.webp",
  },
  {
    slug: "larkhall",
    name: "Larkhall",
    location: "bath",
    tagline: "Village-within-a-city with a loyal community",
    description:
      "Larkhall is a self-contained neighbourhood north-east of Bath city centre, with its own high street, primary school, and a fiercely loyal community. The area has a genuine village feel rare for a city neighbourhood, with local shops, pubs, and a regular farmers' market.\n\nProperty is a mix of Georgian, Victorian, and Edwardian homes, many with gardens. Larkhall is popular with families and professionals who want community and character without the premium of the city centre.",
    avgPrice: "£425,000",
    priceRange: "£200,000 - £850,000",
    propertyTypes: ["Georgian terraces", "Victorian houses", "Edwardian semis", "Cottages"],
    transport: {
      nearestStation: "Bath Spa (20 mins walk or bus)",
      commuteTime: "20 mins to city centre by bus or foot",
      busRoutes: "6, 7 to city centre",
      parking: "Mix of on-street; generally easier than city centre",
    },
    lifestyle: {
      restaurants: "The Larkhall Inn, Top Lock Cafe, local bakery",
      schools: "St Saviour's Primary, St Stephen's Primary",
      greenSpaces: "Alice Park, Kennet & Avon Canal towpath",
      shopping: "Larkhall high street independents",
    },
    highlights: [
      "Genuine village atmosphere within the city",
      "Regular farmers' market and community events",
      "Alice Park playground and green space",
      "Kennet & Avon Canal walks",
      "Strong local identity and community spirit",
    ],
    bestFor: ["Families", "First-time buyers", "Community-minded buyers"],
    imageUrl: "/images/areas/bath/larkhall-800w.webp",
  },
]
